import type { RouteRecordRaw, RouteLocationRaw } from 'vue-router'
import { AutomationRoutes } from '@/routes/user/automations/types'
import { AppModule } from '@/config/types'
import { NEW_AUTOMATION_ID, type AutomationFlowDetailsResolverParams } from '@/services/domain/automations/types'
import type { DomainTitlerParams } from '@/services/route/titlers/types'
import AutomationFlow from '@/data/models/domain/automations/automationFlow'
import type { TmRoutesDictionary } from '@/services/route/routerTypes'

export const getAutomationRoutes = (): RouteRecordRaw[] => [
  {
    name: AutomationRoutes.index,
    path: 'automations',
    meta: {
      modules: [
        AppModule.Automations,
        AppModule.ExternalIntegrations,
        AppModule.TenDlcTollFree, // TollFreePhoneNumbersService via ComposeSenderAlerts → AutomationActionSendSmsSidebar
        AppModule.Tasks, // KanbanService via AutomationActionCreateNewTask sidebars, nodes, and logs
        AppModule.Deals, // DealsKanbanService via AutomationActionCreateNewDeal sidebars, nodes, and logs
        AppModule.WidgetSubscribe, // FormSubscribeService via AutomationTriggerSubscribeFormFilled sidebar, node, and logs
        AppModule.TmCodemirrorEditor,
      ],
      permission: 'automationRules',
    },
    redirect: { name: AutomationRoutes.flows },
    children: [
      {
        name: AutomationRoutes.flows,
        path: 'flows',
        meta: {
          tabTitle: 'pageTabTitle.user.automations.flows',
        },
        component: () => import('@/components/views/automations/AutomationFlows.vue'),
      },
      {
        name: AutomationRoutes.flowDetails,
        path: 'flow-details/:automationId?',
        meta: {
          hidePageLayout: true,
          resolverComponent: () => import('@/components/domain/automations/AutomationFlowDetailsResolver.vue'),
          resolvers: [
            {
              service: 'AutomationFlowDetailsResolver',
              params: {
                templateIdRequestParam: 'templateId',
                automationIdRequestParam: 'automationId',
                fillFromRequestParam: 'fillFrom',
              } satisfies AutomationFlowDetailsResolverParams,
            },
            {
              service: 'UserNumbersResolverService',
            },
          ],
          titler: {
            service: 'DomainTitlerService',
            params: {
              model: AutomationFlow,
              field: 'name',
              idParamName: 'automationId',
              noRecordTranslateKey: 'pageTabTitle.user.automations.newAutomationFlow',
            } as DomainTitlerParams,
          },
        },
        props: (route) => {
          const query = route.query as unknown as NonNullable<
            TmRoutesDictionary[typeof AutomationRoutes.flowDetails]['query']
          >
          return {
            templateId: query.templateId,
            automationId: route.params.automationId || NEW_AUTOMATION_ID,
            isOpenedSettings: query.openedSettings === 'true',
            isOpenedAiBuilder: query.openedAiBuilder === 'true',
          }
        },
        component: () => import('@/components/views/automations/AutomationFlowDetails.vue'),
      },
      {
        name: AutomationRoutes.logs,
        path: 'logs',
        meta: {
          tabTitle: 'pageTabTitle.user.automations.logs',
        },
        component: () => import('@/components/views/automations/AutomationLogs.vue'),
      },
      {
        name: AutomationRoutes.templates,
        path: 'templates',
        meta: {
          tabTitle: 'pageTabTitle.user.automations.templates',
        },
        component: () => import('@/components/views/automations/AutomationTemplates.vue'),
      },
    ],
  },
]

export const getAutomationFlowsRouteLocation = () => {
  return {
    name: AutomationRoutes.flows,
  } satisfies RouteLocationRaw
}

export const getAutomationFlowCreationRouteLocation = (templateId?: string, fillFrom?: 'sms' | 'email') => {
  return {
    name: AutomationRoutes.flowDetails,
    query: { templateId, fillFrom },
  } satisfies RouteLocationRaw
}

export const getAutomationFlowAiBuilderCreationRouteLocation = () => {
  return {
    name: AutomationRoutes.flowDetails,
    query: { openedAiBuilder: 'true' },
  } satisfies RouteLocationRaw
}

export const getAutomationFlowRouteLocation = (automationId: string) => {
  return {
    name: AutomationRoutes.flowDetails,
    params: { automationId },
  } satisfies RouteLocationRaw
}
